import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { logoGreen } from 'images';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { IMPERSONATION_BANNER_HEIGHT, useImpersonationSessions } from '@hooks/impersonation';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, Divider, Drawer, IconButton, List, useMediaQuery, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { actions, selectAppConfig } from '@store';

import { COLLAPSED_WIDTH, EXPANDED_WIDTH } from './constants.js';
import { NavSectionItem } from './NavSectionItem.js';
import { useNavConfig } from './useNavConfig.js';

interface NavVerticalProps {
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

export function NavVertical({ mobileOpen = false, onMobileClose }: NavVerticalProps) {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'));

  const dispatch = useDispatch();
  const { sidebarCollapsed: collapsed } = useSelector(selectAppConfig);

  const toggle = useCallback(() => {
    dispatch(actions.setSidebarCollapsed(!collapsed));
  }, [dispatch, collapsed]);

  const onNavigate = isMd ? undefined : onMobileClose;

  const navSections = useNavConfig();

  const isCollapsed = isMd && collapsed;
  const drawerWidth = isCollapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH;

  const { isImpersonating } = useImpersonationSessions();
  const drawerTopOffset = isImpersonating && isMd ? IMPERSONATION_BANNER_HEIGHT : 0;

  const [openSection, setOpenSection] = useState<{ id: string; anchor: HTMLElement } | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelClose = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimerRef.current = setTimeout(() => setOpenSection(null), 60);
  }, [cancelClose]);

  const openSectionFor = useCallback(
    (id: string, anchor: HTMLElement) => {
      cancelClose();
      setOpenSection((prev) => (prev?.id === id && prev.anchor === anchor ? prev : { id, anchor }));
    },
    [cancelClose]
  );

  const closePopover = useCallback(() => {
    cancelClose();
    setOpenSection(null);
  }, [cancelClose]);

  useEffect(
    () => () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    },
    []
  );

  const paperSx = useMemo(
    () => ({
      width: drawerWidth,
      top: drawerTopOffset,
      height: drawerTopOffset ? `calc(100% - ${drawerTopOffset}px)` : '100%',
      transition: 'width 0.2s ease, top 0.2s ease',
      bgcolor: 'grey.800',
      color: theme.palette.common.white,
      border: 'none',
      overflowX: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    }),
    [drawerWidth, drawerTopOffset, theme.palette.common.white]
  );

  return (
    <Drawer
      open={isMd ? true : mobileOpen}
      sx={{ width: drawerWidth, flexShrink: 0, '& .MuiDrawer-paper': paperSx }}
      variant={isMd ? 'permanent' : 'temporary'}
      onClose={onMobileClose}
    >
      {/* Logo + toggle */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: isCollapsed ? 'center' : 'space-between',
          px: isCollapsed ? 1 : 2,
          minHeight: 64,
          flexShrink: 0,
        }}
      >
        {!isCollapsed && (
          <Link style={{ display: 'flex' }} to="/" onClick={onNavigate}>
            <Box
              alt="Spazious"
              component="img"
              src={logoGreen}
              sx={{ height: 28, objectFit: 'contain' }}
            />
          </Link>
        )}
        {isMd && (
          <IconButton
            size="small"
            sx={{
              color: alpha(theme.palette.common.white, 0.5),
              '&:hover': {
                color: theme.palette.common.white,
                bgcolor: alpha(theme.palette.common.white, 0.08),
              },
            }}
            onClick={toggle}
          >
            {isCollapsed ? (
              <ChevronRightIcon fontSize="small" />
            ) : (
              <ChevronLeftIcon fontSize="small" />
            )}
          </IconButton>
        )}
      </Box>

      <Divider sx={{ borderColor: alpha(theme.palette.common.white, 0.08), flexShrink: 0 }} />

      {/* Navigation */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          py: 1,
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: alpha(theme.palette.common.white, 0.1),
            borderRadius: 2,
          },
        }}
      >
        <List dense disablePadding>
          {navSections.map((section) => {
            const isOpenInPopover = isCollapsed && openSection?.id === section.id;
            return (
              <NavSectionItem
                key={section.id}
                cancelClose={cancelClose}
                closePopover={closePopover}
                collapsed={isCollapsed}
                isPopoverOpen={isOpenInPopover}
                popoverAnchor={isOpenInPopover ? openSection.anchor : null}
                requestOpen={openSectionFor}
                scheduleClose={scheduleClose}
                section={section}
                onNavigate={onNavigate}
              />
            );
          })}
        </List>
      </Box>
    </Drawer>
  );
}
